import type { FC, ReactElement } from "react";
import React, { useId, useMemo } from "react";

import cn from "classnames";

import Shape from "./components/Shape";
import {
    pictureSizeToBadgeSizeDict,
    pictureSizeToCounterSizeDict,
    pictureSizeToIconSizeDict,
} from "./utils/main";
import { extractProps, getChildrenRecord } from "../../utils/react";
import { getClipPathStyle } from "../../utils/ui/svg";

import type { PictureProps } from "./types";

import classes from "./Picture.styl";
import Stroke from "./assets/stroke.svg";

export const PICTURE_TEST_ID = "picture-test-id";
const PICTURE_DISPLAY_NAME = "Picture";

/**
 * Компонент для отображения аватарки группы или пользователя. Может содержать изображение, плейсхолдер, иконку или текст. Дополнительно может отображать различные индикаторы в виде компонентов **Picture.Badge** и **Picture.Counter**.
 *
 * Если вам нужен компонент только для отображения изображения, используйте **Illustration**.
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-100539">Спека</a>
 *
 * @deprecated Используйте компонент AvaPicture.
 */
export const Picture: FC<PictureProps> = (props): ReactElement => {
    const {
        view = "primary",
        size = "36",
        content = "image",
        stroke = false,
        "data-tsid": tsid,
        children,
        ...otherProps
    } = props;

    const { src, alt, overlay, customColorsStyles } = extractProps(otherProps, [
        "src",
        "alt",
        "overlay",
        "customColorsStyles",
    ]);

    const withImage = content === "image";
    const withIcon = content === "icon" || overlay;
    const withCounter = size !== "16";
    const withBadge = withCounter && size !== "24";

    const badgeSize = pictureSizeToBadgeSizeDict[size!];
    const counterSize = pictureSizeToCounterSizeDict[size!];
    const iconSize = pictureSizeToIconSizeDict[size!];

    const { Badge, Counter, Icon } = useMemo(
        () =>
            getChildrenRecord(children, "Picture", {
                Badge: {
                    props: {
                        size: badgeSize,
                    },
                    skipElement: !badgeSize,
                },
                Counter: {
                    props: {
                        size: counterSize,
                    },
                    skipElement: !counterSize,
                },
                Icon: {
                    props: {
                        size: iconSize,
                        pictureSize: size,
                    },
                    required: withIcon,
                },
            }),
        [badgeSize, children, counterSize, iconSize, size, withIcon],
    );

    const pictureClassName = cn(
        classes["picture"],
        classes[`__size-${size}`],
        classes[`__view-${view}`],
        {
            [classes["__with-overlay"]]: overlay,
        },
        customColorsStyles,
    );

    const shapeId = `shape-${useId()}`;

    return (
        <div
            className={pictureClassName}
            data-tsid={tsid || PICTURE_TEST_ID}
            data-uikit={PICTURE_DISPLAY_NAME}
        >
            <Shape className={classes["picture__shape"]} id={shapeId} />

            {withIcon && <div className={classes["picture__icon"]}>{Icon}</div>}
            {withCounter && (
                <div className={classes["picture__counter"]}>{Counter}</div>
            )}
            {withBadge && (
                <div className={classes["picture__badge"]}>{Badge}</div>
            )}
            <div
                className={classes["picture__content"]}
                style={getClipPathStyle(shapeId)}
            >
                {stroke && <Stroke className={classes["picture__stroke"]} />}
                {withImage && <img src={src} alt={alt} />}
            </div>
        </div>
    );
};

Picture.displayName = PICTURE_DISPLAY_NAME;

export default Picture;
